import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useUserStore } from '@entities/user';
import { chatApi } from './chatApi';
import type { ChunkMeta, Message, Thread } from '../model/types';

export const chatQueryKeys = {
  all: ['chat'] as const,
  threads: (assistantId: string) => [...chatQueryKeys.all, 'threads', assistantId] as const,
  messages: (threadId: string) => [...chatQueryKeys.all, 'messages', threadId] as const,
  chunksMeta: (ids: string[]) =>
    [...chatQueryKeys.all, 'chunksMeta', [...ids].sort().join(',')] as const,
};

export function useThreads(assistantId: string | undefined) {
  return useQuery<Thread[], Error>({
    queryKey: chatQueryKeys.threads(assistantId ?? ''),
    queryFn: () => chatApi.listThreads(assistantId!),
    enabled: !!assistantId,
    staleTime: 30_000,
  });
}

export function useMessages(threadId: string | undefined) {
  return useQuery<Message[], Error>({
    queryKey: chatQueryKeys.messages(threadId ?? ''),
    queryFn: () => chatApi.getMessages(threadId!),
    enabled: !!threadId,
    // Не stale — после каждой мутации обновляем оптимистично
    staleTime: Infinity,
  });
}

export function useCreateThreadMutation(assistantId: string) {
  const qc = useQueryClient();
  return useMutation<Thread, Error, void>({
    mutationFn: () => chatApi.createThread(assistantId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: chatQueryKeys.threads(assistantId) });
    },
  });
}

/**
 * Отправка сообщения с оптимистичным обновлением.
 *
 * Логика:
 * 1) Сразу добавляем user-сообщение в кэш (видно мгновенно).
 * 2) Ждём ответа бэка (он вернёт assistant-сообщение, user уже сохранил у себя).
 * 3) Дозагружаем историю целиком — гарантия согласованности
 *    (на случай если оптимистичный user отличается от того, что записал бэк).
 *
 * Если бэк ответил ошибкой — оптимистичный user удаляется через rollback.
 */
export function useSendMessageMutation(threadId: string) {
  const qc = useQueryClient();
  const queryKey = chatQueryKeys.messages(threadId);
  const setBalance = useUserStore((s) => s.setBalance);

  return useMutation<
    Message,
    Error,
    { content: string; model_id?: number },
    { previous: Message[] | undefined }
  >({
    mutationFn: ({ content, model_id }) => chatApi.sendMessage(threadId, content, model_id),

    onMutate: async ({ content }) => {
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<Message[]>(queryKey);

      const optimisticUser: Message = {
        id: `optimistic-${Date.now()}`,
        thread_id: threadId,
        role: 'user',
        content,
        created_at: new Date().toISOString(),
        retrieved_chunk_ids: null,
        image_ids: null,
      };

      qc.setQueryData<Message[]>(queryKey, (old) => [...(old ?? []), optimisticUser]);
      return { previous };
    },

    onError: (_err, _vars, context) => {
      if (context?.previous) {
        qc.setQueryData(queryKey, context.previous);
      }
    },

    onSuccess: (assistantMsg) => {
      // Бэк вернул только assistant-сообщение. user уже в кэше оптимистично,
      // но его id «optimistic-...» — заменим всю историю запросом целиком,
      // чтобы получить честные id и created_at от бэка.
      qc.setQueryData<Message[]>(queryKey, (old) => {
        if (!old) return [assistantMsg];
        // Удаляем оптимистичные user-сообщения и добавляем настоящий assistant
        const cleaned = old.filter((m) => !m.id.startsWith('optimistic-'));
        return [...cleaned, assistantMsg];
      });
      // Перезапросим историю в фоне для согласованности (id user-сообщения)
      qc.invalidateQueries({ queryKey });
      // V2-1: тест-чат платный — баланс после списания приходит в ответе.
      // (/user после входа не перезапрашивается — обновляем стор напрямую.)
      if (assistantMsg.balance_after_rub != null) {
        setBalance(assistantMsg.balance_after_rub);
      }
    },
  });
}

export function useDeleteThreadMutation(assistantId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (threadId) => chatApi.deleteThread(threadId),
    onSuccess: (_void, threadId) => {
      qc.removeQueries({ queryKey: chatQueryKeys.messages(threadId) });
      qc.invalidateQueries({ queryKey: chatQueryKeys.threads(assistantId) });
    },
  });
}

/**
 * Метаданные чанков для citations.
 *
 * Если endpoint /knowledge/chunks?ids= ещё не применён на бэке (mini-PR не доехал)
 * — будет 404, мы это перехватываем и возвращаем пустой массив. UI просто
 * не отрисует плашки, основной чат продолжит работать.
 */
export function useChunksMeta(ids: string[]) {
  return useQuery<ChunkMeta[], Error>({
    queryKey: chatQueryKeys.chunksMeta(ids),
    queryFn: async () => {
      try {
        return await chatApi.getChunksMeta(ids);
      } catch (err) {
        const status = (err as { response?: { status?: number } }).response?.status;
        // 404 ожидаем если mini-PR ещё не применён — возвращаем пусто, не падаем
        if (status === 404) return [];
        throw err;
      }
    },
    enabled: ids.length > 0,
    staleTime: 5 * 60_000, // метаданные чанков редко меняются
  });
}
